import { useCallback, useEffect, useRef, useState } from 'react'
import { BoxTruckVerificationStatus } from '@numeo/types-core'
import {
    BoxTruckVerification,
    BoxTruckVerificationAudience,
    claimBoxTruckDriver,
    fetchBoxTruckVerifications,
    setBoxTruckVerificationStatus,
} from '../services/boxTruckLoadsApi'

const VERIFICATIONS_PAGE_SIZE = 50

interface UseBoxTruckVerificationsResult {
    items: BoxTruckVerification[]
    total: number
    /** Initial / filter-or-search-change load (replaces the list). */
    loading: boolean
    /** Appending the next page (infinite scroll) — keeps the current rows on screen. */
    loadingMore: boolean
    /** More rows remain past what's loaded. */
    hasMore: boolean
    error: string | null
    /** Active status filter (undefined = all). */
    statusFilter: BoxTruckVerificationStatus | undefined
    setStatusFilter: (status: BoxTruckVerificationStatus | undefined) => void
    /** Active equipment filter (empty = all) — server-resolved like status/search. */
    equipmentFilter: string
    setEquipmentFilter: (equipment: string) => void
    /** Committed (debounced) search term across name/email/phone. */
    search: string
    setSearch: (q: string) => void
    /** Load + append the next page (called on scroll near the bottom). No-op while loading or fully loaded. */
    loadMore: () => void
    refresh: () => void
    /** Flip a driver's status; the row is updated in place on success. */
    setStatus: (userId: string, status: BoxTruckVerificationStatus) => Promise<void>
    /** Instant-claim this driver into the operator's box-truck roster (no migration/accept). The driver keeps
     *  their own account; the row flips to claimStatus 'added' on success. `leadSource` (optional) records how
     *  they were sourced. */
    claimDriver: (row: BoxTruckVerification, leadSource?: string) => Promise<{ driverId?: string }>
}

/**
 * Owns the operator's Box Truck driver-verification queue: an infinite-scroll list (append per page),
 * filterable by status and searchable by name/email/phone, that flips a row's status in place (map-replace)
 * so an activate/deactivate reflects immediately without a full refetch.
 */
export const useBoxTruckVerifications = (audience?: BoxTruckVerificationAudience): UseBoxTruckVerificationsResult => {
    const [items, setItems] = useState<BoxTruckVerification[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Start on Pending: this is a review queue, and what it is opened for is the applications nobody has
    // decided on yet. The whole history is one chip away — starting there instead meant every approved
    // driver was in front of the decision the operator came to make.
    const [statusFilter, setStatusFilterState] = useState<BoxTruckVerificationStatus | undefined>('pending')
    const [equipmentFilter, setEquipmentFilter] = useState('')
    const [search, setSearch] = useState('')
    // seqRef: monotonic request id — only the LATEST load applies its result, so an in-flight append can't
    // land after a filter/search reset and append stale rows / a wrong total. loadingRef: synchronous latch
    // so a burst of scroll events can't double-fire the same append page.
    const seqRef = useRef(0)
    const loadingRef = useRef(false)

    const load = useCallback(
        async (targetPage: number, status: BoxTruckVerificationStatus | undefined, searchTerm: string, append: boolean, equipment?: string) => {
            if (append && loadingRef.current) return
            loadingRef.current = true
            const seq = ++seqRef.current
            if (append) setLoadingMore(true)
            else setLoading(true)
            setError(null)
            try {
                const { items: rows, total: count } = await fetchBoxTruckVerifications(
                    targetPage,
                    VERIFICATIONS_PAGE_SIZE,
                    status,
                    searchTerm,
                    audience,
                    equipment || undefined
                )
                if (seq !== seqRef.current) return // superseded by a newer load — drop this stale result
                setItems((prev) => (append ? [...prev, ...rows] : rows))
                setTotal(count)
                setPage(targetPage)
            } catch (e) {
                if (seq === seqRef.current) setError(e instanceof Error ? e.message : 'Failed to load verifications')
            } finally {
                if (seq === seqRef.current) {
                    if (append) setLoadingMore(false)
                    else setLoading(false)
                }
                loadingRef.current = false
            }
        },
        [audience]
    )

    // Reset to page 1 whenever a filter or the (debounced) search term changes.
    useEffect(() => {
        void load(1, statusFilter, search, false, equipmentFilter)
    }, [load, statusFilter, search, equipmentFilter])

    const setStatusFilter = useCallback((status: BoxTruckVerificationStatus | undefined) => setStatusFilterState(status), [])
    const refresh = useCallback(() => void load(1, statusFilter, search, false, equipmentFilter), [load, statusFilter, search, equipmentFilter])

    const loadMore = useCallback(() => {
        if (loading || loadingMore || items.length >= total) return
        void load(page + 1, statusFilter, search, true, equipmentFilter)
    }, [load, loading, loadingMore, items.length, total, page, statusFilter, search, equipmentFilter])

    const setStatus = useCallback(async (userId: string, status: BoxTruckVerificationStatus) => {
        try {
            const updated = await setBoxTruckVerificationStatus(userId, status)
            setItems((prev) => prev.map((v) => (v.userId === userId ? { ...v, boxTruckInfo: updated } : v)))
        } catch (e) {
            // Log, then re-throw so the caller (page) can surface it to the operator — the row is left
            // untouched, so nothing shows as changed when the write didn't land.
            console.error('[box-truck] setBoxTruckVerificationStatus failed', e)
            throw e
        }
    }, [])

    const claimDriver = useCallback(async (row: BoxTruckVerification, leadSource?: string) => {
        try {
            // INSTANT claim (no migration): the driver becomes this operator's box-truck driver immediately,
            // keeping their own account. The operator then controls their App Access from My Drivers.
            const claimed = await claimBoxTruckDriver(row.userId, leadSource)
            setItems((prev) => prev.map((v) => (v.userId === row.userId ? { ...v, claimStatus: 'added' } : v)))
            return claimed
        } catch (e) {
            // 409 = another operator won the claim race. The server's queue-visibility cache can serve this
            // row for up to its TTL after the rival claim, so the button LOOKS actionable — drop the row
            // locally so the stale affordance self-heals instead of 409ing on every retry.
            if ((e as { response?: { status?: number } })?.response?.status === 409) {
                setItems((prev) => prev.filter((v) => v.userId !== row.userId))
            }
            console.error('[box-truck] claimDriver failed', e)
            throw e
        }
    }, [])

    return {
        items,
        total,
        loading,
        loadingMore,
        hasMore: items.length < total,
        error,
        statusFilter,
        setStatusFilter,
        equipmentFilter,
        setEquipmentFilter,
        search,
        setSearch,
        loadMore,
        refresh,
        setStatus,
        claimDriver,
    }
}
